/**
 * Recent activity feed component.
 * Shows list of recent property actions with timestamps.
 */

import { useTranslation } from 'react-i18next';
import { Plus, Pencil } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Activity {
  propertyId: string;
  title: string;
  action: string;
  timestamp: string;
  ownerName?: string;
}

interface ActivityFeedProps {
  activities: Activity[];
}

/**
 * Get the appropriate icon for an activity action.
 */
function getActionIcon(action: string) {
  switch (action.toLowerCase()) {
    case 'created':
      return Plus;
    case 'updated':
    default:
      return Pencil;
  }
}

/**
 * Get the badge color class for an activity action.
 */
function getActionBadgeClass(action: string): string {
  switch (action.toLowerCase()) {
    case 'created':
      return 'bg-green-100 text-green-700';
    case 'updated':
    default:
      return 'bg-amber-100 text-amber-700';
  }
}

export function ActivityFeed({ activities }: ActivityFeedProps) {
  const { t } = useTranslation('dashboard');

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold text-[var(--pv-color-primary)]">
        {t('company.recentActivity.title')}
      </h2>

      {activities.length === 0 ? (
        <div className="rounded-xl bg-white p-6 text-center shadow-sm">
          <p className="text-gray-500">{t('company.empty.noActivity')}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm">
          {activities.map((activity, index) => {
            const Icon = getActionIcon(activity.action);
            const badgeClass = getActionBadgeClass(activity.action);
            const isLast = index === activities.length - 1;

            // Format timestamp as relative time
            let timeAgo: string;
            try {
              timeAgo = formatDistanceToNow(new Date(activity.timestamp), {
                addSuffix: true,
              });
            } catch {
              timeAgo = activity.timestamp;
            }

            return (
              <div
                key={`${activity.propertyId}-${activity.timestamp}`}
                className={`flex items-start gap-3 p-3 ${
                  !isLast ? 'border-b border-gray-100' : ''
                }`}
              >
                {/* Icon */}
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
                  <Icon className="h-4 w-4 text-gray-600" />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gray-900">
                    {activity.title}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-gray-500">{timeAgo}</span>
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs font-medium ${badgeClass}`}
                    >
                      {activity.action === 'created'
                        ? t('company.recentActivity.created')
                        : t('company.recentActivity.updated')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
